import Vue from "vue";
import axios from "@/plugins/axios";
import { isSolarSolutionsDevice } from "@/helpers/sensorTypes";

function unwrapResponse(response) {
  const payload = response && response.data ? response.data : {};

  if (payload && payload.success === false) {
    const apiError = payload.error || {};
    const err = new Error(
      apiError.message || payload.message || "solar-actions-service-unavailable"
    );
    err.isApiError = true;
    err.code = apiError.code || null;
    err.fields = apiError.fields || null;
    err.status = response && response.status ? Number(response.status) : null;
    throw err;
  }

  if (Object.prototype.hasOwnProperty.call(payload, "data")) {
    return payload.data;
  }

  if (payload && Object.prototype.hasOwnProperty.call(payload, "success")) {
    return payload.success;
  }

  return payload;
}

function parseApiError(err, fallbackMessageKey = "oops") {
  if (
    err &&
    typeof err === "object" &&
    Object.prototype.hasOwnProperty.call(err, "message") &&
    Object.prototype.hasOwnProperty.call(err, "retryable")
  ) {
    return err;
  }

  if (err && err.isApiError) {
    return {
      code: err.code || null,
      message: err.message || fallbackMessageKey,
      fields: err.fields || null,
      status: err.status || null,
      retryable: err.code === "SERVER_ERROR" || Number(err.status) === 502,
    };
  }

  const status =
    err && err.response && err.response.status
      ? Number(err.response.status)
      : null;
  const payload = err && err.response ? err.response.data || {} : {};
  const apiError = payload && payload.error ? payload.error : {};

  const code = apiError.code || null;
  const message =
    apiError.message || payload.message || err.message || fallbackMessageKey;
  const fields = apiError.fields || null;
  const retryable = code === "SERVER_ERROR" || status === 502;

  return { code, message, fields, status, retryable };
}

function normalizeRunPayload(data) {
  const normalizeList = (list) =>
    (Array.isArray(list) ? list : []).map((run) => normalizeRunItem(run));

  if (Array.isArray(data)) {
    return { list: normalizeList(data), meta: null };
  }

  if (data && Array.isArray(data.data)) {
    return {
      list: normalizeList(data.data),
      meta: {
        current_page: data.current_page,
        last_page: data.last_page,
        per_page: data.per_page,
        total: data.total,
      },
    };
  }

  return { list: [], meta: null };
}

function buildAttachmentUpsertBody(payload = {}) {
  const {
    solar_device_action_id,
    sort_order,
    external_device_id,
    phone_number,
    variables,
  } = payload;

  const body = { solar_device_action_id };

  if (sort_order !== null && sort_order !== undefined && sort_order !== "") {
    body.sort_order = sort_order;
  }

  const normalizedPhoneNumber =
    phone_number !== null && phone_number !== undefined && phone_number !== ""
      ? phone_number
      : external_device_id;

  if (
    normalizedPhoneNumber !== null &&
    normalizedPhoneNumber !== undefined &&
    normalizedPhoneNumber !== ""
  ) {
    body.phone_number = normalizedPhoneNumber;
  }

  if (variables && typeof variables === "object") {
    body.variables = variables;
  }

  return body;
}

function normalizeRunItem(run) {
  if (!run || typeof run !== "object") return run;

  const command =
    run.command && typeof run.command === "object" ? run.command : {};
  const responses = Array.isArray(run.responses) ? run.responses : [];

  return {
    ...run,
    command,
    responses,
    request_payload:
      run.request_payload !== undefined
        ? run.request_payload
        : command && Object.keys(command).length
        ? command
        : null,
    response_payload:
      run.response_payload !== undefined ? run.response_payload : responses,
    normalized_output:
      run.normalized_output !== undefined
        ? run.normalized_output
        : responses.length
        ? responses
        : null,
  };
}

function normalizeSchedule(schedule) {
  if (!schedule || typeof schedule !== "object") {
    return null;
  }

  const canonicalId = schedule.id || schedule.remote_schedule_id || null;

  return {
    ...schedule,
    // Remote-first: canonical schedule id for all UI actions/routes
    id: canonicalId,
    remote_schedule_id: schedule.remote_schedule_id || canonicalId,
    local_schedule_id:
      schedule.local_schedule_id !== undefined
        ? schedule.local_schedule_id
        : null,
    interval_value:
      schedule.interval_value !== undefined ? schedule.interval_value : null,
    interval_unit: schedule.interval_unit || null,
    start_at: schedule.start_at || null,
    is_active: !!schedule.is_active,
  };
}

function normalizeScheduleListPayload(data) {
  const list = Array.isArray(data)
    ? data
    : data && Array.isArray(data.data)
    ? data.data
    : [];

  return list
    .map((item) => normalizeSchedule(item))
    .filter((item) => !!item && !!item.id);
}

function extractBackendErrorMessage(err) {
  const apiError = parseApiError(err, "solar-actions-schedule-action-failed");
  if (apiError.retryable) {
    return "solar-actions-service-unavailable";
  }
  return apiError.message;
}

function isFresh(timestamp, maxAgeMs) {
  if (!timestamp) return false;
  return Date.now() - Number(timestamp) <= Number(maxAgeMs || 0);
}

function buildRunsCacheKey(
  slug,
  { status, trigger_source, page, solar_device_action_id } = {}
) {
  return [
    slug || "",
    status || "",
    trigger_source || "",
    page || "",
    solar_device_action_id || "",
  ].join("|");
}

export const state = {
  attachedBySlug: {},
  loadingBySlug: {},
  actionsFetchedAtBySlug: {},
  actionAttachmentsById: {},
  actionAttachmentsLoadingById: {},
  attachmentsFetchedAtById: {},
  runsBySlug: {},
  runsByKey: {},
  runsLoadingBySlug: {},
  runsFetchedAtBySlug: {},
  runsFetchedAtByKey: {},
  runDetails: {},
  schedulesByActionSlug: {},
  schedulesLoadingBySlug: {},
  schedulesFetchedAtByKey: {},
};

export const mutations = {
  SET_DEVICE_ACTIONS_LOADING(state, { slug, value }) {
    Vue.set(state.loadingBySlug, slug, value);
  },
  SET_DEVICE_ACTIONS(state, { slug, actions }) {
    Vue.set(state.attachedBySlug, slug, Array.isArray(actions) ? actions : []);
  },
  SET_DEVICE_ACTIONS_FETCHED_AT(state, { slug, ts }) {
    Vue.set(state.actionsFetchedAtBySlug, slug, Number(ts) || 0);
  },
  SET_ACTION_ATTACHMENTS_LOADING(state, { actionId, value }) {
    Vue.set(state.actionAttachmentsLoadingById, String(actionId), !!value);
  },
  SET_ACTION_ATTACHMENTS(state, { actionId, items }) {
    Vue.set(
      state.actionAttachmentsById,
      String(actionId),
      Array.isArray(items) ? items : []
    );
  },
  SET_ACTION_ATTACHMENTS_FETCHED_AT(state, { actionId, ts }) {
    Vue.set(state.attachmentsFetchedAtById, String(actionId), Number(ts) || 0);
  },
  SET_DEVICE_RUNS_LOADING(state, { slug, value }) {
    Vue.set(state.runsLoadingBySlug, slug, value);
  },
  SET_DEVICE_RUNS(state, { slug, runs }) {
    Vue.set(state.runsBySlug, slug, Array.isArray(runs) ? runs : []);
  },
  SET_DEVICE_RUNS_FETCHED_AT(state, { slug, ts }) {
    Vue.set(state.runsFetchedAtBySlug, slug, Number(ts) || 0);
  },
  PREPEND_DEVICE_RUN(state, { slug, run }) {
    const list = state.runsBySlug[slug] ? [...state.runsBySlug[slug]] : [];
    if (run) {
      list.unshift(run);
    }
    Vue.set(state.runsBySlug, slug, list);
  },
  SET_RUN_DETAILS(state, { runId, run }) {
    Vue.set(state.runDetails, runId, run || null);
  },
  SET_SCHEDULES_LOADING(state, { key, value }) {
    Vue.set(state.schedulesLoadingBySlug, key, value);
  },
  SET_SCHEDULES(state, { key, schedules }) {
    Vue.set(
      state.schedulesByActionSlug,
      key,
      Array.isArray(schedules) ? schedules : []
    );
  },
  SET_DEVICE_RUNS_BY_KEY(state, { key, runs }) {
    Vue.set(state.runsByKey, key, Array.isArray(runs) ? runs : []);
  },
  SET_DEVICE_RUNS_FETCHED_AT_BY_KEY(state, { key, ts }) {
    Vue.set(state.runsFetchedAtByKey, key, Number(ts) || 0);
  },
  SET_SCHEDULES_FETCHED_AT(state, { key, ts }) {
    Vue.set(state.schedulesFetchedAtByKey, key, Number(ts) || 0);
  },
};

export const actions = {
  async fetchDeviceSolarActions({ commit }, slug) {
    commit("SET_DEVICE_ACTIONS_LOADING", { slug, value: true });
    try {
      const res = await axios.get(`/devices/${slug}/actions`);
      const data = unwrapResponse(res);
      commit("SET_DEVICE_ACTIONS", { slug, actions: data || [] });
      commit("SET_DEVICE_ACTIONS_FETCHED_AT", { slug, ts: Date.now() });
      return { ok: true, data: data || [] };
    } catch (err) {
      console.error("fetchDeviceSolarActions failed", err);
      return { ok: false, error: parseApiError(err) };
    } finally {
      commit("SET_DEVICE_ACTIONS_LOADING", { slug, value: false });
    }
  },

  async fetchDeviceSolarActionsIfNeeded(
    { dispatch, getters },
    { slug, force = false, maxAgeMs = 60000 } = {}
  ) {
    if (!slug) return { ok: false, error: { message: "Missing device slug" } };

    const existing = getters.actionsBySlug(slug);
    const fetchedAt = getters.actionsFetchedAtBySlug(slug);
    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("fetchDeviceSolarActions", slug);
  },

  async fetchActionAttachmentsAcrossDevices(
    { commit, dispatch, rootGetters },
    actionId
  ) {
    const normalizedActionId = Number(actionId);
    commit("SET_ACTION_ATTACHMENTS_LOADING", {
      actionId: normalizedActionId,
      value: true,
    });

    try {
      let devicesMap = rootGetters["devices/alldevices"] || {};
      if (!devicesMap || !Object.keys(devicesMap).length) {
        await dispatch("devices/fetchDevices", null, { root: true });
        devicesMap = rootGetters["devices/alldevices"] || {};
      }

      const devices = Object.values(devicesMap || {}).filter(
        (device) => !!device && !!device.slug && isSolarSolutionsDevice(device)
      );

      const attached = [];

      await Promise.all(
        devices.map(async (device) => {
          const slug = device.slug;
          const result = await dispatch("fetchDeviceSolarActions", slug);
          if (!result || !result.ok || !Array.isArray(result.data)) return;

          const found = result.data.find(
            (item) => Number(item.id) === normalizedActionId
          );
          if (!found) return;

          attached.push({
            ...found,
            slug,
            device_name: (device.view && device.view.name) || device.name || "",
            device_mac:
              device.identifier || device.mac || device.mac_address || "",
            is_enabled: !found.pivot || found.pivot.is_enabled !== false,
          });
        })
      );

      attached.sort((a, b) =>
        String(a.device_name).localeCompare(String(b.device_name))
      );

      commit("SET_ACTION_ATTACHMENTS", {
        actionId: normalizedActionId,
        items: attached,
      });
      commit("SET_ACTION_ATTACHMENTS_FETCHED_AT", {
        actionId: normalizedActionId,
        ts: Date.now(),
      });

      return { ok: true, data: attached };
    } catch (err) {
      console.error("fetchActionAttachmentsAcrossDevices failed", err);
      return { ok: false, error: parseApiError(err) };
    } finally {
      commit("SET_ACTION_ATTACHMENTS_LOADING", {
        actionId: normalizedActionId,
        value: false,
      });
    }
  },

  async fetchActionAttachmentsAcrossDevicesIfNeeded(
    { dispatch, getters },
    { actionId, force = false, maxAgeMs = 60000 } = {}
  ) {
    const normalizedActionId = Number(actionId);
    if (!normalizedActionId) {
      return { ok: false, error: { message: "Missing action id" } };
    }

    const existing = getters.actionAttachmentsById(normalizedActionId);
    const fetchedAt =
      getters.actionAttachmentsFetchedAtById(normalizedActionId);
    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("fetchActionAttachmentsAcrossDevices", normalizedActionId);
  },

  async attachSolarActionToDevice({ dispatch }, payload) {
    try {
      const { slug } = payload;
      const body = buildAttachmentUpsertBody(payload);

      const res = await axios.post(`/devices/${slug}/actions/attach`, body);
      const data = unwrapResponse(res);
      await dispatch("fetchDeviceSolarActions", slug);
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-attach-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("attachSolarActionToDevice failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    }
  },

  async updateAttachedSolarActionToDevice({ dispatch }, payload) {
    try {
      const { slug } = payload;
      const body = buildAttachmentUpsertBody(payload);

      const res = await axios.post(`/devices/${slug}/actions/attach`, body);
      const data = unwrapResponse(res);
      await dispatch("fetchDeviceSolarActions", slug);
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-attach-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("updateAttachedSolarActionToDevice failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    }
  },

  async deactivateDeviceSolarAction({ dispatch }, { slug, actionId }) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/deactivate`
      );
      const data = unwrapResponse(res);
      await dispatch("fetchDeviceSolarActions", slug);
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-device-deactivate-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("deactivateDeviceSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    }
  },

  async triggerDeviceSolarAction(
    { commit, dispatch },
    { slug, actionId, payload }
  ) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/trigger`,
        payload || {}
      );
      const data = unwrapResponse(res);

      if (data && data.id) {
        commit("PREPEND_DEVICE_RUN", { slug, run: data });
      } else {
        await dispatch("fetchDeviceSolarActionRuns", { slug });
      }

      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-trigger-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("triggerDeviceSolarAction failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: apiError.retryable
          ? "solar-actions-service-unavailable"
          : apiError.message,
      });
      return { ok: false, error: apiError };
    }
  },

  async fetchDeviceSolarActionRuns(
    { commit },
    {
      slug,
      per_page,
      page,
      solar_device_action_id,
      status,
      trigger_source,
      date_from,
      date_to,
    }
  ) {
    commit("SET_DEVICE_RUNS_LOADING", { slug, value: true });
    try {
      const params = [];
      if (per_page) params.push(`per_page=${encodeURIComponent(per_page)}`);
      if (page) params.push(`page=${encodeURIComponent(page)}`);
      if (solar_device_action_id) {
        params.push(
          `solar_device_action_id=${encodeURIComponent(solar_device_action_id)}`
        );
      }
      if (status) params.push(`status=${encodeURIComponent(status)}`);
      if (trigger_source) {
        params.push(`trigger_source=${encodeURIComponent(trigger_source)}`);
      }
      if (date_from) params.push(`date_from=${encodeURIComponent(date_from)}`);
      if (date_to) params.push(`date_to=${encodeURIComponent(date_to)}`);
      const query = params.length ? `?${params.join("&")}` : "";
      const res = await axios.get(`/devices/${slug}/actions/runs${query}`);
      const data = unwrapResponse(res);
      const normalized = normalizeRunPayload(data);
      const ts = Date.now();

      // Legacy slug-based cache (used by PREPEND_DEVICE_RUN + DeviceSolarActionsSection)
      commit("SET_DEVICE_RUNS", { slug, runs: normalized.list });
      commit("SET_DEVICE_RUNS_FETCHED_AT", { slug, ts });

      // Per-filter composite-key cache (used by SolarActionDetailsPage filter tabs)
      const cacheKey = buildRunsCacheKey(slug, {
        status,
        trigger_source,
        page,
        solar_device_action_id,
      });
      commit("SET_DEVICE_RUNS_BY_KEY", {
        key: cacheKey,
        runs: normalized.list,
      });
      commit("SET_DEVICE_RUNS_FETCHED_AT_BY_KEY", { key: cacheKey, ts });

      return { ok: true, data: normalized };
    } catch (err) {
      console.error("fetchDeviceSolarActionRuns failed", err);
      return { ok: false, error: parseApiError(err) };
    } finally {
      commit("SET_DEVICE_RUNS_LOADING", { slug, value: false });
    }
  },

  async fetchDeviceSolarActionRunsIfNeeded(
    { dispatch, getters },
    {
      slug,
      per_page,
      page,
      solar_device_action_id,
      status,
      trigger_source,
      date_from,
      date_to,
      force = false,
      maxAgeMs = 30000,
    } = {}
  ) {
    if (!slug) return { ok: false, error: { message: "Missing device slug" } };

    // Use the composite key so every unique filter combination has its own cache
    const cacheKey = buildRunsCacheKey(slug, {
      status,
      trigger_source,
      page,
      solar_device_action_id,
    });
    const fetchedAt = getters.runsFetchedAtByParams(cacheKey);
    const existing = getters.runsByParams(cacheKey);

    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: { list: existing, meta: null }, cached: true };
    }

    return dispatch("fetchDeviceSolarActionRuns", {
      slug,
      per_page,
      page,
      solar_device_action_id,
      status,
      trigger_source,
      date_from,
      date_to,
    });
  },

  async fetchDeviceSolarActionRun({ commit }, { slug, runId }) {
    try {
      const res = await axios.get(`/devices/${slug}/actions/runs/${runId}`);
      const raw = unwrapResponse(res);
      const data = normalizeRunItem(raw);
      commit("SET_RUN_DETAILS", { runId, run: data || null });
      return { ok: true, data };
    } catch (err) {
      console.error("fetchDeviceSolarActionRun failed", err);
      return { ok: false, error: parseApiError(err) };
    }
  },

  async listSchedules({ commit }, { slug, actionId }) {
    const key = `${slug}_${actionId}`;
    commit("SET_SCHEDULES_LOADING", { key, value: true });
    try {
      const res = await axios.get(
        `/devices/${slug}/actions/${actionId}/schedules`
      );
      const data = unwrapResponse(res);
      const schedules = normalizeScheduleListPayload(data);
      commit("SET_SCHEDULES", { key, schedules });
      commit("SET_SCHEDULES_FETCHED_AT", { key, ts: Date.now() });
      return { ok: true, data: schedules };
    } catch (err) {
      console.error("listSchedules failed", err);
      return { ok: false, error: parseApiError(err) };
    } finally {
      commit("SET_SCHEDULES_LOADING", { key, value: false });
    }
  },

  async listSchedulesIfNeeded(
    { dispatch, getters },
    { slug, actionId, force = false, maxAgeMs = 60000 } = {}
  ) {
    if (!slug || !actionId) {
      return { ok: false, error: { message: "Missing slug/actionId" } };
    }

    const key = `${slug}_${actionId}`;
    const existing = getters.schedulesByActionSlug(slug, actionId);
    const fetchedAt = getters.schedulesFetchedAtByKey(key);

    if (
      !force &&
      Array.isArray(existing) &&
      fetchedAt &&
      isFresh(fetchedAt, maxAgeMs)
    ) {
      return { ok: true, data: existing, cached: true };
    }

    return dispatch("listSchedules", { slug, actionId });
  },

  async createSchedule({ dispatch }, { slug, actionId, payload }) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/schedules/create`,
        payload
      );
      const data = unwrapResponse(res);
      await dispatch("listSchedules", { slug, actionId });
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-schedule-create-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("createSchedule failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: extractBackendErrorMessage(apiError),
      });
      return { ok: false, error: apiError };
    }
  },

  async pauseSchedule({ dispatch }, { slug, actionId, scheduleId }) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/schedules/${scheduleId}/pause`
      );
      const data = unwrapResponse(res);
      await dispatch("listSchedules", { slug, actionId });
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-schedule-pause-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("pauseSchedule failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: extractBackendErrorMessage(apiError),
      });
      return { ok: false, error: apiError };
    }
  },

  async resumeSchedule({ dispatch }, { slug, actionId, scheduleId, payload }) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/schedules/${scheduleId}/resume`,
        payload || {}
      );
      const data = unwrapResponse(res);
      await dispatch("listSchedules", { slug, actionId });
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-schedule-resume-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("resumeSchedule failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: extractBackendErrorMessage(apiError),
      });
      return { ok: false, error: apiError };
    }
  },

  async deleteSchedule({ dispatch }, { slug, actionId, scheduleId }) {
    try {
      const res = await axios.post(
        `/devices/${slug}/actions/${actionId}/schedules/${scheduleId}/delete`
      );
      const data = unwrapResponse(res);
      await dispatch("listSchedules", { slug, actionId });
      this.dispatch("general/setAlert", {
        type: "success",
        msg: "solar-actions-schedule-delete-success",
      });
      return { ok: true, data };
    } catch (err) {
      const apiError = parseApiError(err);
      console.error("deleteSchedule failed", err);
      this.dispatch("general/setAlert", {
        type: "failed",
        msg: extractBackendErrorMessage(apiError),
      });
      return { ok: false, error: apiError };
    }
  },
};

export const getters = {
  actionsBySlug: (state) => (slug) => state.attachedBySlug[slug] || [],
  actionsLoadingBySlug: (state) => (slug) => !!state.loadingBySlug[slug],
  actionsFetchedAtBySlug: (state) => (slug) =>
    Number(state.actionsFetchedAtBySlug[slug] || 0),
  actionAttachmentsById: (state) => (actionId) =>
    state.actionAttachmentsById[String(actionId)] || [],
  actionAttachmentsLoadingById: (state) => (actionId) =>
    !!state.actionAttachmentsLoadingById[String(actionId)],
  actionAttachmentsFetchedAtById: (state) => (actionId) =>
    Number(state.attachmentsFetchedAtById[String(actionId)] || 0),
  runsBySlug: (state) => (slug) => {
    const list = state.runsBySlug[slug] || [];
    return [...list].sort((a, b) => {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime; // newest first
    });
  },
  runsLoadingBySlug: (state) => (slug) => !!state.runsLoadingBySlug[slug],
  runsFetchedAtBySlug: (state) => (slug) =>
    Number(state.runsFetchedAtBySlug[slug] || 0),
  runById: (state) => (id) => state.runDetails[id] || null,
  schedulesByActionSlug: (state) => (slug, actionId) =>
    state.schedulesByActionSlug[`${slug}_${actionId}`] || [],
  schedulesLoadingBySlug: (state) => (slug, actionId) =>
    !!state.schedulesLoadingBySlug[`${slug}_${actionId}`],
  schedulesFetchedAtByKey: (state) => (key) =>
    Number(state.schedulesFetchedAtByKey[key] || 0),
  // Per-filter composite-key getters — used by SolarActionDetailsPage
  runsByParams: (state) => (key) => {
    const list = state.runsByKey[key] || [];
    return [...list].sort((a, b) => {
      const aTime = a.started_at ? new Date(a.started_at).getTime() : 0;
      const bTime = b.started_at ? new Date(b.started_at).getTime() : 0;
      return bTime - aTime; // newest first
    });
  },
  runsFetchedAtByParams: (state) => (key) =>
    Number(state.runsFetchedAtByKey[key] || 0),
};
